import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { method } from "@/data/portfolio";

/**
 * About.
 *
 * The method used to be four one word abstractions (Sustainability,
 * Restoration, Vernacular, Warmth) set in huge serif with a paragraph beside
 * each. Those four nouns appear on every practice site on earth and commit to
 * nothing, which is why no amount of spacing made the section feel designed.
 * Each row is now a claim with a receipt: a specific thing done, the material
 * or number that proves it, and a link to the project it happened on.
 *
 * The structure is deliberately unlike anything else on the page. Every other
 * section is heading over grid; this one is a sticky left column holding the
 * argument while the evidence scrolls past it. That contrast is doing more work
 * than any amount of polish inside the old layout could.
 */
export const Manifesto = () => {
  return (
    <section
      id="manifesto"
      className="relative bg-cream text-ink py-24 md:py-32"
      data-testid="manifesto-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 md:gap-x-10">
          {/* Sticky argument */}
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-[112px]">
              <p className="chapter-num">About</p>
              <h2 className="mt-5 font-display text-display-l text-ink max-w-[14ch]">
                How I actually work.
              </h2>
              <p className="mt-7 font-sans font-light text-base md:text-lg text-ink2 leading-relaxed max-w-[36ch]">
                Four things I will not trade away, and the projects where you can
                go and check them.
              </p>

              {/* The Churchill quote moved to the home band at her request, so
                  the studio's own line holds this spot instead of repeating it. */}
              <figure className="mt-10 border-t border-ink/15 pt-6 max-w-[34ch]">
                <blockquote className="font-display italic text-xl md:text-2xl leading-[1.3] text-ink2">
                  “Designed to run, not just to open.”
                </blockquote>
                <figcaption className="mt-3 label text-ink2">
                  The studio, in one line
                </figcaption>
              </figure>
            </div>
          </div>

          {/* Evidence */}
          <div className="col-span-12 lg:col-span-7 lg:col-start-6">
            <ul>
              {method.map((m, i) => (
                <motion.li
                  key={m.n}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-12% 0px" }}
                  transition={{
                    duration: 0.9,
                    delay: (i % 2) * 0.05,
                    ease: [0.19, 1, 0.22, 1],
                  }}
                  className="grid grid-cols-12 gap-5 md:gap-8 py-9 first:pt-0 border-b border-ink/15 last:border-b-0"
                  data-testid={`method-${m.n}`}
                >
                  <Link
                    to={`/work/${m.slug}`}
                    className="img-frame col-span-4 sm:col-span-3 aspect-square self-start"
                    data-cursor="view"
                    aria-label={`${m.evidence}, view project`}
                    tabIndex={-1}
                  >
                    <img
                      src={m.image}
                      alt={m.evidence}
                      loading="lazy"
                      width={900}
                      height={900}
                    />
                  </Link>

                  <div className="col-span-8 sm:col-span-9">
                    <p className="label text-ink2">
                      {m.n}
                    </p>
                    <h3 className="mt-2 font-display text-display-m text-ink">
                      {m.label}
                    </h3>
                    <p className="mt-3 font-sans font-light text-[15px] md:text-base text-ink2 leading-relaxed max-w-[54ch]">
                      {m.body}
                    </p>
                    <Link
                      to={`/work/${m.slug}`}
                      data-cursor="hover"
                      data-testid={`method-link-${m.n}`}
                      className="mt-4 inline-block label text-terracotta2 hover:text-ink transition-colors editorial-link"
                    >
                      {m.evidence}
                    </Link>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bio */}
        <div className="mt-24 md:mt-36 grid grid-cols-12 gap-8 md:gap-14 items-start">
          <div className="col-span-12 md:col-span-4">
            <p className="chapter-num">Biography</p>
            {/* shruthi-portrait-crop.jpg is a 4:5 crop generated once from the
                639x844 original, taken from (190,150) so it clears the dark arch
                edge baked into that frame. Cropping the file means no scale or
                object-position gymnastics here, and no risk of the framing
                drifting when the container size changes. */}
            <figure className="mt-6 img-frame aspect-[4/5] max-w-[340px]">
              <img
                src="/portfolio/img/shruthi-portrait-crop.jpg"
                alt="Shruthi Ravi"
                width={720}
                height={900}
                loading="lazy"
                data-testid="about-portrait"
              />
            </figure>
            <figcaption className="mt-3 label text-ink2">
              Shruthi Ravi · Bangalore
            </figcaption>
          </div>

          <div className="col-span-12 md:col-span-8">
            {/* One statement at display scale, then the supporting record. The
                previous version was three paragraphs at one size and one colour,
                which is why it read as a resume rather than as writing. */}
            <p className="font-display text-display-m text-ink max-w-[30ch]">
              I trained as an architect, then went and ran the buildings.
            </p>

            <div className="mt-8 space-y-5 font-sans font-light text-base md:text-lg text-ink2 leading-relaxed max-w-[62ch]">
              <p>
                Seven years across three studios before my own practice.
                Commercial work at Sanctuary Architects, from cafes and flower
                shops to a members only bar. Furniture and lighting for Katiba,
                across co-working spaces and restaurants. Then Dwar Eco Design,
                where I worked on an eleven acre resort built from shipping
                containers, joint developments and restoration.
              </p>
              <p>
                In 2017 I was given the Entrepreneur of the Year award for The
                Teal Door Cafe, four storeys I built to employ women who have
                endured abuse, with every piece of furniture and lighting
                upcycled and made by hand. In 2018 I took that story to a TEDx
                stage. I have also run a women led sustainable clothing line.
              </p>
              <p className="text-ink">
                What I am actually chasing is the moment a client walks into the
                finished room. That half breath, half smile. Everything else is
                drawings.
              </p>
            </div>
          </div>
        </div>

        {/* Record. Every line below is in her own portfolio document. */}
        <div className="mt-20 md:mt-28">
          <p className="chapter-num">The record</p>
          <ul className="mt-7">
            {[
              {
                y: "2020 to today",
                t: "Independent practice",
                r: "Architecture · Interiors · Furniture",
                d: "Private residences, heritage restoration, and four hospitality businesses of my own across Bangalore, Goa and Austin.",
              },
              {
                y: "2016 to 2020",
                t: "Dwar Eco Design",
                r: "Architect and Interior Designer",
                d: "An eleven acre resort built from repurposed shipping containers. Residential work, joint developments and restoration, in vernacular design and natural materials.",
              },
              {
                y: "2015 to 2018",
                t: "Katiba",
                r: "Furniture and Lighting Designer",
                d: "Furniture and lighting at scale for co-working spaces, residences and commercial venues, holding one design language across all of them.",
              },
              {
                y: "2013 to 2015",
                t: "Sanctuary Architects",
                r: "Intern, Interior Design",
                d: "Commercial work from cafes and flower shops to a members only bar, several residential projects, and the lead on a kids room.",
              },
            ].map((r) => (
              <li
                key={r.t}
                className="grid grid-cols-12 gap-y-2 gap-x-6 md:gap-x-10 py-7 border-t border-ink/15 last:border-b"
                data-testid={`record-${r.t.split(" ")[0].toLowerCase()}`}
              >
                <p className="col-span-12 md:col-span-2 label text-ink2 md:pt-1.5">
                  {r.y}
                </p>
                <div className="col-span-12 md:col-span-3">
                  <p className="font-display text-display-s text-ink">{r.t}</p>
                  <p className="mt-1 label text-terracotta2">
                    {r.r}
                  </p>
                </div>
                <p className="col-span-12 md:col-span-7 font-sans font-light text-[15px] md:text-base text-ink2 leading-relaxed">
                  {r.d}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
